import {
  Circle,
  ExternalLink,
  LoaderCircle,
  RefreshCw,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AUTH_TOKEN_KEY,
  CHROME_WEBSTORE_URL,
  SELECTED_WORKSPACE_KEY,
} from "../../constants";
import { StorageService } from "../../services";
import {
  getWebExtensionStatus,
  getWebExtensionSettingsUrl,
  openWebExtension,
  openWebExtensionSettings,
  syncWebExtensionAuth,
} from "../../services/WebExtensionBridge";
import { isExtensionRuntime } from "../../utils/appSurface";
import {
  shouldForceOutdatedStatus,
  type WebExtensionStatus,
} from "../../utils/extensionStatus";
import ReactTooltip from "./ReactTooltip";

type WebExtensionSectionProps = {
  isDesktopCollapsed: boolean;
};

const statusContent: Record<
  WebExtensionStatus,
  { label: string; icon: React.ReactNode }
> = {
  checking: {
    label: "Checking Extension",
    icon: (
      <LoaderCircle aria-hidden="true" size={20} className="animate-spin" />
    ),
  },
  open: {
    label: "Open Extension",
    icon: <ExternalLink aria-hidden="true" size={20} />,
  },
  update: {
    label: "Open Extension",
    icon: <ExternalLink aria-hidden="true" size={20} />,
  },
  get: {
    label: "Get the Extension",
    icon: <ExternalLink aria-hidden="true" size={20} />,
  },
};

const WebExtensionSection: React.FC<WebExtensionSectionProps> = ({
  isDesktopCollapsed,
}) => {
  const location = useLocation();
  const [status, setStatus] = useState<WebExtensionStatus>("checking");
  const [isOpening, setIsOpening] = useState(false);

  const refreshStatus = useCallback(async () => {
    setStatus(await getWebExtensionStatus());
  }, []);

  useEffect(() => {
    if (isExtensionRuntime()) return;
    void refreshStatus();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void refreshStatus();
    };
    window.addEventListener("focus", refreshStatus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", refreshStatus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshStatus]);

  if (isExtensionRuntime()) return null;

  const forceOutdated = shouldForceOutdatedStatus(location.search);
  const displayStatus = forceOutdated
    ? "update"
    : isOpening
      ? "checking"
      : status;
  const content = statusContent[displayStatus];

  const handleClick = async () => {
    if (status === "checking" || isOpening) return;

    if (status === "get" && !forceOutdated) {
      window.open(CHROME_WEBSTORE_URL, "_blank", "noopener,noreferrer");
      return;
    }

    setIsOpening(true);
    try {
      const openPromise = openWebExtension();
      const [token, workspaceId] = await Promise.all([
        StorageService.getStorageItem<string>(AUTH_TOKEN_KEY),
        StorageService.getStorageItem<string>(SELECTED_WORKSPACE_KEY),
      ]);
      await Promise.all([
        openPromise,
        token
          ? syncWebExtensionAuth(token, workspaceId)
          : Promise.resolve(false),
      ]);
    } finally {
      setIsOpening(false);
    }
  };

  const handleOpenSettings = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();
    const settingsUrl = event.currentTarget.href;
    const opened = await openWebExtensionSettings();
    if (!opened) {
      window.open(settingsUrl, "_blank", "noopener,noreferrer");
    }
  };

  const actionIcon =
    displayStatus === "update" && isDesktopCollapsed ? (
      <RefreshCw aria-hidden="true" size={20} />
    ) : (
      content.icon
    );
  const tooltipLabel =
    displayStatus === "update" ? "Update Extension" : content.label;

  return (
    <div className="ui-drawer-extension-section">
      {displayStatus === "get" && !isDesktopCollapsed && (
        <p className="ui-extension-availability-label mb-2 text-center text-xxs font-normal leading-3 text-body-muted">
          Available in the Chrome Web Store
        </p>
      )}
      {displayStatus === "update" && !isDesktopCollapsed && (
        <p className="ui-extension-update-label mb-2 flex items-start justify-center gap-1 text-center text-xxs font-normal leading-3 text-body-muted">
          <Circle
            aria-hidden="true"
            size={8}
            fill="currentColor"
            className="ui-extension-update-notice-icon mt-[2px] shrink-0"
          />
          <span>
            Newer Version available. Update in{" "}
            <a
              href={getWebExtensionSettingsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="ui-extension-settings-link font-semibold underline underline-offset-2"
              onClick={handleOpenSettings}
            >
              Chrome Settings
            </a>
            .
          </span>
        </p>
      )}
      <button
        id="web-extension-action"
        type="button"
        className={`ui-extension-action ${displayStatus === "get" ? "ui-extension-action-get" : ""} ${displayStatus === "update" ? "ui-extension-action-update" : ""}`.trim()}
        onClick={handleClick}
        disabled={displayStatus === "checking"}
        aria-label={content.label}
      >
        <span className="ui-extension-action-label">{content.label}</span>
        {actionIcon}
        {displayStatus === "update" && isDesktopCollapsed && (
          <Circle
            aria-hidden="true"
            size={10}
            fill="currentColor"
            className="ui-extension-update-indicator"
          />
        )}
      </button>
      {isDesktopCollapsed && (
        <ReactTooltip anchorSelect="#web-extension-action" place="right">
          {tooltipLabel}
        </ReactTooltip>
      )}
    </div>
  );
};

export default WebExtensionSection;
